/**
 * Activity Heatmap Export Utilities
 *
 * Provides functionality to export heatmap visualizations as images.
 * Supports PNG format with customizable filenames and quality settings.
 *
 * Renders via `modern-screenshot` (`domToPng`) rather than html2canvas:
 * html2canvas ships its own CSS parser, which throws on the modern color
 * functions Tailwind v4 emits (`oklch()`, `lab()`). modern-screenshot
 * serializes the DOM into an SVG `<foreignObject>` and lets the browser
 * rasterize it, so any color function the browser understands renders
 * correctly. See dcyfr-labs#710.
 */

import { domToPng } from 'modern-screenshot';

// ============================================================================
// TYPES
// ============================================================================

export interface ExportHeatmapOptions {
  /** Element to export (should be the heatmap container) */
  element: HTMLElement;
  /** Filename without extension (default: "activity-heatmap-YYYY-MM-DD") */
  filename?: string;
  /** Image format (currently only PNG supported) */
  format?: 'png';
  /** PNG quality (0-1, default: 1.0 for maximum quality) */
  quality?: number;
  /** Background color (default: transparent for PNG) */
  backgroundColor?: string | null;
  /** Scale multiplier for higher resolution (default: 2 for retina) */
  scale?: number;
}

export interface ExportResult {
  /** Whether the export succeeded */
  success: boolean;
  /** Error message if export failed */
  error?: string;
  /** Generated filename */
  filename?: string;
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Generate default filename with current date
 *
 * @example
 * ```ts
 * generateDefaultFilename() // "activity-heatmap-2025-12-25"
 * ```
 */
export function generateDefaultFilename(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `activity-heatmap-${year}-${month}-${day}`;
}

/**
 * Download data URL as file
 *
 * Creates a temporary anchor element to trigger browser download.
 *
 * @param dataUrl - Data URL from canvas.toDataURL()
 * @param filename - Filename with extension
 */
function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

// ============================================================================
// EXPORT FUNCTION
// ============================================================================

/**
 * Export heatmap element as PNG image
 *
 * Uses modern-screenshot's `domToPng` to rasterize the DOM element,
 * then downloads as PNG file.
 *
 * Features:
 * - High-resolution export (2x scale for retina displays)
 * - Preserves colors and styling (including oklch/lab via the browser)
 * - Transparent background by default
 * - Auto-generated filename with date
 *
 * @param options - Export configuration
 * @returns Promise resolving to export result
 *
 * @example
 * ```tsx
 * const heatmapRef = useRef<HTMLDivElement>(null);
 *
 * const handleExport = async () => {
 *   if (!heatmapRef.current) return;
 *
 *   const result = await exportHeatmapAsImage({
 *     element: heatmapRef.current,
 *     filename: "my-activity-2025",
 *     quality: 1.0,
 *   });
 *
 *   if (!result.success) {
 *     console.error(result.error);
 *   }
 * };
 * ```
 */
export async function exportHeatmapAsImage(options: ExportHeatmapOptions): Promise<ExportResult> {
  const {
    element,
    filename = generateDefaultFilename(),
    format = 'png',
    quality = 1.0,
    backgroundColor = null,
    scale = 2,
  } = options;

  try {
    // Validate element
    if (!element) {
      return {
        success: false,
        error: 'Element not found or not mounted',
      };
    }

    // Guard format before rasterizing (PNG is the only supported output)
    if (format !== 'png') {
      return {
        success: false,
        error: `Unsupported format: ${format}`,
      };
    }

    // Rasterize the DOM element to a PNG data URL. modern-screenshot renders
    // via an SVG <foreignObject>, so the browser resolves modern color
    // functions (oklch/lab) that html2canvas could not parse.
    const dataUrl = await domToPng(element, {
      // `scale` multiplies output resolution (retina-quality export)
      scale,
      // `null` (transparent) → undefined so modern-screenshot keeps it transparent
      backgroundColor: backgroundColor ?? undefined,
      // PNG is lossless; `quality` only affects lossy formats but is harmless
      quality,
      // Allow time for fonts/images to embed before rasterizing
      timeout: 15000,
    });

    const fullFilename = `${filename}.${format}`;

    // Trigger download
    downloadDataUrl(dataUrl, fullFilename);

    return {
      success: true,
      filename: fullFilename,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error during export';

    console.error('Heatmap export failed:', error);

    return {
      success: false,
      error: errorMessage,
    };
  }
}
